import type { UserInputsByMetadataId } from '@server/user-inputs';
import type {
  DocumentationTagAndMetadataElementsDto,
  DocumentationUserAndMetadataElementsDto,
  TagDto,
} from '../../types/documentationUserInputs';
import { UserInputTypes } from '@server/user-inputs';
import omit from 'lodash/omit';
import { configurationItemMapFromApiMetadataElements } from '../../components/documentation/tags-center/countsFromMetadataElements';
import { userInputMetadataElementId } from '../../components/documentation/tags-center/utils';
import type { ConfigurationItemMap } from '../../components/documentation/configuration-item-tables/shared/helpers';

export const tagEntityForTagsList = (tag: DocumentationTagAndMetadataElementsDto): TagDto => {
  return omit(tag, 'metadataElements');
};

export const userEntriesFromTagsAndUsersApi = (
  users: DocumentationUserAndMetadataElementsDto[],
): Pick<DocumentationUserAndMetadataElementsDto, 'userId'>[] => {
  return users.map((u) => ({ userId: u.userId }));
};

export const tagConfigurationsForTagFromApi = (
  tag: DocumentationTagAndMetadataElementsDto,
): { [key in UserInputTypes]: ConfigurationItemMap } => {
  return {
    [UserInputTypes.Tags]: configurationItemMapFromApiMetadataElements(tag.metadataElements),
    [UserInputTypes.Owner]: {},
    [UserInputTypes.Collaborators]: {},
  };
};

export const tagConfigurationsForUserFromApi = (
  user: DocumentationUserAndMetadataElementsDto,
): { [key in UserInputTypes]: ConfigurationItemMap } => {
  return {
    [UserInputTypes.Tags]: {},
    [UserInputTypes.Owner]: configurationItemMapFromApiMetadataElements(user.ownerMetadataElements),
    [UserInputTypes.Collaborators]: configurationItemMapFromApiMetadataElements(
      user.collaboratorMetadataElements,
    ),
  };
};

export const buildUserInputsByMetadataId = (
  tags: DocumentationTagAndMetadataElementsDto[],
  users: DocumentationUserAndMetadataElementsDto[],
): UserInputsByMetadataId => {
  const result: UserInputsByMetadataId = {};

  const ensureEntry = (metadataId: string) => {
    if (!result[metadataId]) {
      result[metadataId] = {
        [UserInputTypes.Tags]: [],
        [UserInputTypes.Owner]: '',
        [UserInputTypes.Collaborators]: [],
      };
    }
  };

  for (const tag of tags) {
    for (const el of tag.metadataElements) {
      const elementId = userInputMetadataElementId(el);
      ensureEntry(elementId);
      result[elementId][UserInputTypes.Tags].push(tag.id);
    }
  }

  for (const user of users) {
    for (const el of user.ownerMetadataElements) {
      const elementId = userInputMetadataElementId(el);
      ensureEntry(elementId);
      result[elementId][UserInputTypes.Owner] = user.userId;
    }
    for (const el of user.collaboratorMetadataElements) {
      const elementId = userInputMetadataElementId(el);
      ensureEntry(elementId);
      result[elementId][UserInputTypes.Collaborators].push(user.userId);
    }
  }

  return result;
};
